// src/pages/Appli/AppliAdmin/ClubModal.jsx

import React, { useEffect, useState } from "react";

import {
  createClub,
  getClubById,
  updateClub,
} from "../../../services/clubServices";

import { useNotification } from "../../../components/Notification/NotificationManager";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "../../../components/Modal/Modal";

import {
  FaSave,
  FaTimes,
  FaUsers,
} from "react-icons/fa";

const NA_DEPS = [
  { code: "16", nom: "Charente" },
  { code: "17", nom: "Charente-Maritime" },
  { code: "19", nom: "Corrèze" },
  { code: "23", nom: "Creuse" },
  { code: "24", nom: "Dordogne" },
  { code: "33", nom: "Gironde" },
  { code: "40", nom: "Landes" },
  { code: "47", nom: "Lot-et-Garonne" },
  { code: "64", nom: "Pyrénées-Atlantiques" },
  { code: "79", nom: "Deux-Sèvres" },
  { code: "86", nom: "Vienne" },
  { code: "87", nom: "Haute-Vienne" },
];

const EMPTY = {
  nom_club: "",
  departement_club: "",
  adresse_club: "",
  tel_club: "",
  email_club: "",
  site_club: "",
  logo_url: "",
  numero_club: "",
  nombre_adherents: 0,
  coordonnees_gps: "",
};

function nullableString(value) {
  const normalizedValue = String(value ?? "").trim();

  return normalizedValue === "" ? null : normalizedValue;
}

function clubToForm(club) {
  if (!club) return EMPTY;

  return {
    nom_club: club.nom_club ?? "",
    departement_club: club.departement_club ?? "",
    adresse_club: club.adresse_club ?? "",
    tel_club: club.tel_club ?? "",
    email_club: club.email_club ?? "",
    site_club: club.site_club ?? "",
    logo_url: club.logo_url ?? "",
    numero_club: club.numero_club ?? "",
    nombre_adherents: club.nombre_adherents ?? 0,
    coordonnees_gps: club.coordonnees_gps ?? "",
  };
}

function buildPayload(form) {
  return {
    nom_club: String(form.nom_club || "").trim(),
    departement_club: String(
      form.departement_club || "",
    ).trim(),
    adresse_club: nullableString(form.adresse_club),
    tel_club: nullableString(form.tel_club),
    email_club: nullableString(form.email_club),
    site_club: nullableString(form.site_club),
    logo_url: nullableString(form.logo_url),
    numero_club: String(form.numero_club || "").trim(),
    nombre_adherents: Number(form.nombre_adherents || 0),
    coordonnees_gps: nullableString(form.coordonnees_gps),
  };
}

const ClubModal = ({
  isOpen,
  onClose,
  isEditMode,
  clubId,
  onRefresh,
}) => {
  const { addNotification } = useNotification();

  const [form, setForm] = useState(EMPTY);
  const [loadingClub, setLoadingClub] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!isEditMode || !clubId) {
        setForm(EMPTY);
        return;
      }

      try {
        setLoadingClub(true);

        const data = await getClubById(clubId);

        if (!cancelled) {
          setForm(clubToForm(data));
        }
      } catch (error) {
        console.error(error);

        if (!cancelled) {
          addNotification(
            "Erreur lors du chargement du club",
            "error",
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingClub(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    isEditMode,
    clubId,
    addNotification,
  ]);

  const onChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (
      !form.nom_club?.trim() ||
      !form.departement_club?.trim() ||
      !form.numero_club?.trim()
    ) {
      addNotification(
        "Renseignez au minimum : Nom du club, Département et N° club.",
        "error",
      );

      return;
    }

    const nombreAdherents = Number(form.nombre_adherents);

    if (
      !Number.isInteger(nombreAdherents) ||
      nombreAdherents < 0
    ) {
      addNotification(
        "Le nombre de licenciés doit être un entier positif ou nul.",
        "error",
      );

      return;
    }

    try {
      setSaving(true);

      const payload = buildPayload(form);

      if (isEditMode) {
        await updateClub(clubId, payload);

        addNotification(
          "Club mis à jour avec succès",
          "success",
        );
      } else {
        await createClub(payload);

        addNotification(
          "Club créé avec succès",
          "success",
        );
      }

      await onRefresh?.();

      onClose();
    } catch (error) {
      console.error(error);

      const message =
        error?.response?.data?.message ||
        "Erreur lors de l’enregistrement du club";

      addNotification(message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClasses = [
    "mt-1",
    "w-full",
    "rounded-lg",
    "border",
    "border-slate-300",
    "bg-white",
    "px-3",
    "py-2",
    "text-slate-900",
    "outline-none",
    "transition",
    "focus:border-indigo-500",
    "focus:ring-4",
    "focus:ring-indigo-500/10",
    "dark:border-slate-700",
    "dark:bg-slate-950",
    "dark:text-white",
  ].join(" ");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      labelledById="club-modal-title"
      describedById="club-modal-desc"
      initialFocusSelector="#club-name"
    >
      <ModalHeader
        id="club-modal-title"
        title={
          isEditMode
            ? "Modifier un club"
            : "Ajouter un club"
        }
        subtitle="Complétez les informations du club"
        onClose={onClose}
      />

      <ModalBody id="club-modal-desc">
        {loadingClub ? (
          <div className="flex min-h-[250px] items-center justify-center">
            <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-indigo-600" />
              Chargement du club…
            </div>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="max-h-[70vh] space-y-4 overflow-y-auto pr-1"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs uppercase text-slate-500">
                  Nom du club{" "}
                  <span className="text-pink-600">*</span>
                </label>

                <input
                  id="club-name"
                  name="nom_club"
                  value={form.nom_club}
                  onChange={onChange}
                  className={inputClasses}
                  required
                />
              </div>

              <div>
                <label className="text-xs uppercase text-slate-500">
                  Département{" "}
                  <span className="text-pink-600">*</span>
                </label>

                <select
                  name="departement_club"
                  value={form.departement_club}
                  onChange={onChange}
                  className={inputClasses}
                  required
                >
                  <option value="">
                    Sélectionner
                  </option>

                  {NA_DEPS.map((departement) => (
                    <option
                      key={departement.code}
                      value={departement.code}
                    >
                      {departement.code} - {departement.nom}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs uppercase text-slate-500">
                  Adresse{" "}
                  <span className="opacity-60">
                    (optionnel)
                  </span>
                </label>

                <input
                  name="adresse_club"
                  value={form.adresse_club}
                  onChange={onChange}
                  className={inputClasses}
                  placeholder="Ex : 12 rue du Dojo, 33000 Bordeaux"
                />
              </div>

              <div>
                <label className="text-xs uppercase text-slate-500">
                  Téléphone{" "}
                  <span className="opacity-60">
                    (optionnel)
                  </span>
                </label>

                <input
                  name="tel_club"
                  value={form.tel_club}
                  onChange={onChange}
                  className={inputClasses}
                  placeholder="Ex : 06 12 34 56 78"
                />
              </div>

              <div>
                <label className="text-xs uppercase text-slate-500">
                  Email{" "}
                  <span className="opacity-60">
                    (optionnel)
                  </span>
                </label>

                <input
                  type="email"
                  name="email_club"
                  value={form.email_club}
                  onChange={onChange}
                  className={inputClasses}
                  placeholder="contact@club.fr"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs uppercase text-slate-500">
                  Site internet{" "}
                  <span className="opacity-60">
                    (optionnel)
                  </span>
                </label>

                <input
                  type="url"
                  name="site_club"
                  value={form.site_club}
                  onChange={onChange}
                  className={inputClasses}
                  placeholder="https://www.monclub.fr"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs uppercase text-slate-500">
                  Logo URL{" "}
                  <span className="opacity-60">
                    (optionnel)
                  </span>
                </label>

                <input
                  name="logo_url"
                  value={form.logo_url}
                  onChange={onChange}
                  className={inputClasses}
                  placeholder="https://…"
                />

                {(form.logo_url || "").trim() !== "" ? (
                  <img
                    src={form.logo_url}
                    alt="Aperçu du logo"
                    className="mt-2 h-20 w-20 rounded-full object-cover ring-1 ring-slate-200"
                  />
                ) : null}
              </div>

              <div>
                <label className="text-xs uppercase text-slate-500">
                  N° club{" "}
                  <span className="text-pink-600">*</span>
                </label>

                <input
                  name="numero_club"
                  value={form.numero_club}
                  onChange={onChange}
                  className={inputClasses}
                  required
                />
              </div>

              <div>
                <label className="text-xs uppercase text-slate-500">
                  Nombre de licenciés
                </label>

                <div className="relative">
                  <FaUsers className="pointer-events-none absolute left-3 top-[17px] text-sm text-indigo-500" />

                  <input
                    type="number"
                    name="nombre_adherents"
                    value={form.nombre_adherents}
                    onChange={onChange}
                    min="0"
                    step="1"
                    inputMode="numeric"
                    className={`${inputClasses} pl-9`}
                    placeholder="0"
                  />
                </div>

                <p className="mt-1 text-[11px] leading-4 text-slate-400">
                  Ce nombre servira aux futurs votes pondérés.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs uppercase text-slate-500">
                  Coordonnées GPS{" "}
                  <span className="opacity-60">
                    (optionnel)
                  </span>
                </label>

                <input
                  name="coordonnees_gps"
                  value={form.coordonnees_gps}
                  onChange={onChange}
                  className={inputClasses}
                  placeholder="latitude,longitude"
                />
              </div>
            </div>

            <ModalFooter>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-slate-100 px-4 text-slate-800 hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-indigo-600 px-4 text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
                ) : (
                  <FaSave />
                )}

                {saving
                  ? "Enregistrement…"
                  : isEditMode
                    ? "Enregistrer"
                    : "Créer"}
              </button>
            </ModalFooter>
          </form>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ClubModal;